import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../components/common/GradientCircularProgress';
import AccountEventsTable from '../../components/account/AccountEventsTable';
import useBackendApi from '../../hooks/useBackendApi';
import { RequestOptions } from '../../types/httpRequests';
import { AccountEvent, AccountType } from '../../types/account';
import { OrgAccountEventsResponse, OrgAccountEventsResponseItem } from '../../types/api/accountEvents';
import { OrganizationType } from '../../types/organization';
import { useThemeContext } from '../../contexts/ThemeContext.js';
import { useGhostButtonProps } from '../../hooks/useGhostButtonProps';
import { OrgBanner } from './OrgNameDescription';
import BorderedButton from '../../components/common/buttons/BorderedButton';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type OrgAccountEventRow = AccountEvent & {
  account: AccountType;
};

export default function Organization(): JSX.Element {
  const { theme } = useThemeContext();
  const { org_path_name } = useParams<{ org_path_name: string }>();
  const navigate = useNavigate();

  const editButtonProps = useGhostButtonProps(theme.images.editIcon);
  const staffButtonProps = useGhostButtonProps(theme.images.peopleIcon);
  const accountsButtonProps = useGhostButtonProps({
    default: '/icons/store-blue900.svg',
    hover: '/icons/store-blue500.svg',
  });
  const ordersButtonProps = useGhostButtonProps({
    default: '/icons/shopping_cart_light_inactive.svg',
    hover: '/icons/shopping_cart_light_active.svg',
  });

  const { apiFetchRequest, isLoading } = useBackendApi();

  const [org, setOrg] = useState<OrganizationType | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [orgRecentAccountEvents, setOrgRecentAccountEvents] = useState<OrgAccountEventsResponseItem[]>([]);
  const [eventsErrorText, setEventsErrorText] = useState<string | null>(null);
  const [isFetchingEvents, setIsFetchingEvents] = useState<boolean>(false);

  const fail = useCallback(() => setErrorText('Something went wrong. Please try again.'), []);
  const failEvents = useCallback(() => setEventsErrorText('Unable to load events. Please try again.'), []);

  useEffect(() => {
    if (!org_path_name) {
      navigate('/404.html');
      return;
    }

    const fetchOrg = async () => {
      const opts: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}`,
        method: 'GET',
      };

      try {
        const res = await apiFetchRequest(opts);

        if (!res || res.status !== 200) {
          navigate('/404.html');
          return;
        }

        const body = res.body;

        if (body?.message === 'NO_ACCESS' || body.status_code === 401) {
          navigate('/401.html');
          return;
        }
        if (body?.message !== 'SUCCESS' || !body?.organization) {
          fail();
          return;
        }

        setOrg(body.organization);
        setErrorText(null);
      } catch {
        fail();
      }
    };

    fetchOrg();
  }, [apiFetchRequest, fail, navigate, org_path_name]);

  useEffect(() => {
    if (!org_path_name) {
      return;
    }

    const fetchOrgEvents = async () => {
      const queryParams = new URLSearchParams({
        event_type: 'job',
      });

      setIsFetchingEvents(true);

      try {
        const resRecent = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/org-accounts-events?${queryParams.toString()}`,
          method: 'GET',
        });

        if (!resRecent) {
          setOrgRecentAccountEvents([]);
          failEvents();
          return;
        }

        const handleResponse = (res: any) => {
          const { status, body, isError } = res;
          const unauthorized =
            status === 401 ||
            body?.status_code === 401 ||
            body?.message === 'UNAUTHENTICATED' ||
            body?.message === 'UNAUTHORIZED' ||
            body?.message === 'NO_ACCESS';

          if (unauthorized) {
            navigate('/401.html');
            return null;
          }

          if (status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
            return [];
          }

          if (isError || status !== 200) {
            return null;
          }

          const typedBody = body as OrgAccountEventsResponse | undefined;

          if (!typedBody || typedBody.message !== 'SUCCESS' || !Array.isArray(typedBody.account_events)) {
            return [];
          }

          return typedBody.account_events
            .filter((item): item is OrgAccountEventsResponseItem => {
              return Boolean(
                item &&
                  typeof item === 'object' &&
                  (item as OrgAccountEventsResponseItem).account &&
                  Array.isArray((item as OrgAccountEventsResponseItem).events),
              );
            })
            .map((item) => ({
              ...item,
              events: (item.events as AccountEvent[]).filter((event): event is AccountEvent =>
                Boolean(event && typeof event === 'object'),
              ),
            }));
        };

        const recentData = handleResponse(resRecent);

        if (recentData === null) {
          setOrgRecentAccountEvents([]);
          failEvents();
          return;
        }

        setOrgRecentAccountEvents(recentData);
        setEventsErrorText(null);
      } catch {
        setOrgRecentAccountEvents([]);
        failEvents();
      } finally {
        setIsFetchingEvents(false);
      }
    };

    fetchOrgEvents();
  }, [apiFetchRequest, failEvents, navigate, org_path_name]);

  const recentOrgEventRows = useMemo<OrgAccountEventRow[]>(() => {
    const rows: OrgAccountEventRow[] = [];

    orgRecentAccountEvents.forEach((item) => {
      if (!item || !item.account || !Array.isArray(item.events)) {
        return;
      }

      item.events.forEach((event) => {
        if (!event || typeof event !== 'object') {
          return;
        }

        rows.push({
          ...event,
          account: item.account,
        });
      });
    });

    const d = new Date();
    const now = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0);

    const parseTime = (value?: string | null) => {
      if (!value) return 0;
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    // Bucket the closest events (which are already limited to 10 on the database-side) into upcoming and past
    const upcoming = rows.filter((r) => parseTime(r.event_start_date) >= now);
    const past = rows.filter((r) => parseTime(r.event_start_date) < now);

    // Sort upcoming ascending (chronological: closest first)
    upcoming.sort((a, b) => {
      const diff = parseTime(a.event_start_date) - parseTime(b.event_start_date);
      if (diff !== 0) return diff;
      return (a.acc_event_id ?? 0) - (b.acc_event_id ?? 0);
    });

    // Sort past descending (reverse chronological: most recent past first)
    past.sort((a, b) => {
      const diff = parseTime(b.event_start_date) - parseTime(a.event_start_date);
      if (diff !== 0) return diff;
      return (b.acc_event_id ?? 0) - (a.acc_event_id ?? 0);
    });

    return [...upcoming, ...past];
  }, [orgRecentAccountEvents]);

  const handleEventRowClick = useCallback(
    (row: OrgAccountEventRow) => {
      if (!org_path_name) {
        return;
      }

      const accountPath = row.account?.path_name;
      if (!accountPath) {
        return;
      }

      const baseUrl = `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(accountPath)}/events`;

      if (row.acc_event_id) {
        navigate(`${baseUrl}/${encodeURIComponent(String(row.acc_event_id))}`);
      } else {
        navigate(baseUrl);
      }
    },
    [navigate, org_path_name],
  );

  if (isLoading && !org && !errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!org || !org_path_name) {
    return <></>;
  }

  const trimmedOrgDescription = org.description?.trim() ?? '';
  const hasOrgDescription = Boolean(trimmedOrgDescription);
  const orgDescription = hasOrgDescription ? trimmedOrgDescription : 'No description provided';

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'transparent',
          borderRadius: '16px',
          padding: '0px 40px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
          mt: 0,
          width: '100%',
        }}
      >
        <OrgBanner
          org={org}
          description={orgDescription}
          sx={{ padding: '0px', backgroundColor: 'transparent', flex: 1 }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 5, flexWrap: 'wrap' }}>
          <BorderedButton to={`/o/${encodeURIComponent(org_path_name)}/edit`} buttonText="Edit" {...editButtonProps} />
          <BorderedButton
            to={`/o/${encodeURIComponent(org_path_name)}/staff`}
            buttonText="Staff"
            {...staffButtonProps}
          />
          <BorderedButton
            to={`/o/${encodeURIComponent(org_path_name)}/a`}
            buttonText="Accounts"
            {...accountsButtonProps}
          />
          {org_path_name === 'rh' && (
            <BorderedButton
              to={`/o/${encodeURIComponent(org_path_name)}/orders`}
              buttonText="Orders"
              {...ordersButtonProps}
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{ p: 2 }}
        style={{
          backgroundColor: theme.layout.defaults.backgroundColor,
          borderRadius: '16px',
          padding: '40px',
          width: '100%',
        }}
      >
        {eventsErrorText ? (
          <Box>
            <Typography color="error">{eventsErrorText}</Typography>
          </Box>
        ) : isFetchingEvents ? (
          <Box>
            <GradientCircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {recentOrgEventRows.length ? (
              <AccountEventsTable
                title="Calendar"
                events={recentOrgEventRows}
                getRowKey={(row, index) =>
                  row.acc_event_id ?? `${row.account?.acc_id ?? 'account'}-${row.event_start_date ?? index}`
                }
                onRowClick={handleEventRowClick}
                hideColumns={['Type']}
                showAssignedStaff={true}
              />
            ) : (
              <Box>
                <Typography>No recent job events.</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}
